import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useUtmCapture } from "@/lib/utm";
import PublicNav from "@/components/PublicNav";
import { MapPin, Calendar, Users, Search, Sparkles, ChevronLeft, X, AlertCircle, RotateCcw, Clock } from "lucide-react";

const CATEGORIES = [
  "تقنية المعلومات", "إداري", "تسويقي", "مالي ومحاسبي", "قانوني",
  "إعلامي", "موارد بشرية", "بحوث ودراسات", "تعليمي", "صحي",
  "اجتماعي", "هندسي", "خدمة مستفيدين", "قيادي", "إدارة مشاريع",
];
const CITIES = ["الرياض", "جدة", "مكة المكرمة", "المدينة المنورة", "الدمام", "الخبر", "تبوك", "بريدة", "حائل", "جازان"];
const AVAILABILITY = [
  { value: "all", label: "الكل" },
  { value: "open", label: "بها مقاعد" },
  { value: "full", label: "مكتملة العدد" },
];
const DELIVERY_MODES = [
  { value: "", label: "جميع الأنماط" },
  { value: "onsite", label: "حضوري" },
  { value: "remote", label: "عن بُعد" },
  { value: "hybrid", label: "هجين" },
];
const MODE_LABEL = { onsite: "حضوري", remote: "عن بُعد", hybrid: "هجين" };

function opportunityAllocations(o) {
  return Array.isArray(o.association_allocations) ? o.association_allocations : [];
}

function totalAvailableSeats(o) {
  const allocations = opportunityAllocations(o);
  if (allocations.length === 0) {
    return Math.max(0, (o.seats || 0) - (o.seats_filled || 0));
  }
  return allocations.reduce(
    (total, allocation) =>
      total + Math.max(0, (allocation.seats || 0) - (allocation.seats_filled || 0)),
    0,
  );
}

function totalSeats(o) {
  const allocations = opportunityAllocations(o);
  if (allocations.length === 0) return o.seats || 0;
  return allocations.reduce((sum, a) => sum + (a.seats || 0), 0);
}

function distinctCities(o) {
  const allocations = opportunityAllocations(o);
  const cities = allocations
    .filter((a) => (a.delivery_mode || "onsite") !== "remote" && (a.city || "").trim())
    .map((a) => a.city.trim());
  const unique = [...new Set(cities)];
  return unique;
}

function distinctModes(o) {
  const allocations = opportunityAllocations(o);
  if (allocations.length === 0) return [];
  return [...new Set(allocations.map((a) => a.delivery_mode || "onsite"))];
}

export default function OpportunitiesPage({ embedded = false }) {
  useUtmCapture();
  const [params, setParams] = useSearchParams();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const city = params.get("city") || "";
  const mode = params.get("mode") || "";
  const availability = params.get("availability") || "all";

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearFilters = () => setParams({}, { replace: true });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/opportunities?status=published");
      setOpps(data.opportunities);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // NOTE: no auto-city filter is applied. The user opts in explicitly.
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return opps.filter((o) => {
      const allocations = opportunityAllocations(o);
      const cities = distinctCities(o);
      const modes = distinctModes(o);
      const associationNames = allocations
        .map((a) => (a.association_name || "").toLowerCase())
        .filter(Boolean);

      if (s) {
        const haystack = [
          o.title,
          o.description,
          o.category,
          o.city,
          ...cities,
          ...associationNames,
        ].map((t) => (t || "").toLowerCase());
        if (!haystack.some((t) => t.includes(s))) return false;
      }
      if (category && (o.category || "") !== category) return false;

      // City filter — keep remote allocations always visible; match onsite/hybrid on the chosen city
      if (city) {
        if (allocations.length > 0) {
          const hasMatch = allocations.some((a) => {
            const m = a.delivery_mode || "onsite";
            if (m === "remote") return true;
            return (a.city || "").trim() === city;
          });
          if (!hasMatch) return false;
        } else if ((o.city || "") !== city) {
          return false;
        }
      }

      // Delivery-mode filter
      if (mode) {
        if (allocations.length > 0) {
          if (!modes.includes(mode)) return false;
        } else if ((o.delivery_mode || "onsite") !== mode) {
          return false;
        }
      }

      const available = totalAvailableSeats(o);
      if (availability === "open" && available <= 0) return false;
      if (availability === "full" && available > 0) return false;
      return true;
    });
  }, [q, category, city, mode, availability, opps]);

  const activeFilters = [
    q && ["q", q],
    category && ["category", category],
    city && ["city", city],
    mode && ["mode", DELIVERY_MODES.find(m => m.value === mode)?.label],
    availability !== "all" && ["availability", AVAILABILITY.find(a => a.value === availability)?.label],
  ].filter(Boolean);

  return (
    <div className={embedded ? "" : "min-h-screen bg-slate-50"} data-testid="opportunities-page">
      {!embedded && <PublicNav />}

      {/* Hero */}
      {!embedded && (
        <section className="cream-gradient border-b border-cream-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-600">فرص التدريب التعاوني</div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-heading font-black text-slate-900">
              اكتشف فرصة التدريب التعاوني التي تناسبك
            </h1>
            <p className="mt-4 text-lg text-slate-700 max-w-2xl">
              فرص تدريب تعاوني متكاملة تجمع بين المحتوى النظري والتدريب العملي داخل المنظمات، وجلسات إرشادية أسبوعية.
            </p>

            <div className="mt-8 relative max-w-2xl">
              <Search className="h-5 w-5 text-slate-400 absolute top-1/2 -translate-y-1/2 start-4" />
              <input
                type="text"
                value={q}
                onChange={(e) => updateParam("q", e.target.value)}
                placeholder="ابحث بالمجال، التخصص، المدينة، أو اسم الجمعية..."
                className="w-full ps-12 pe-4 py-4 rounded-xl border border-slate-300 bg-white text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="opp-search-input"
              />
            </div>
          </div>
        </section>
      )}

      {embedded && (
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">استكشاف الفرص</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">
            فرص التدريب التعاوني المتاحة
          </h1>
          <p className="mt-2 text-sm text-slate-600">تصفّح الفرص المنشورة، طبّق فلاتر المدينة والنمط، وقدّم مباشرةً.</p>
          <div className="mt-6 relative max-w-2xl">
            <Search className="h-5 w-5 text-slate-400 absolute top-1/2 -translate-y-1/2 start-4" />
            <input
              type="text"
              value={q}
              onChange={(e) => updateParam("q", e.target.value)}
              placeholder="ابحث بالمجال، التخصص، المدينة، أو اسم الجمعية..."
              className="w-full ps-12 pe-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
              data-testid="opp-search-input"
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <section className={embedded ? "" : "border-b border-cream-200 bg-white"}>
        <div className={embedded ? "" : "max-w-7xl mx-auto px-6 lg:px-10 py-6"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">التخصص</label>
              <select
                value={category}
                onChange={(e) => updateParam("category", e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="opp-filter-category"
              >
                <option value="">جميع التخصصات</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">المنطقة</label>
              <select
                value={city}
                onChange={(e) => updateParam("city", e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="opp-filter-city"
              >
                <option value="">كل المدن</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">نمط التدريب</label>
              <select
                value={mode}
                onChange={(e) => updateParam("mode", e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="opp-filter-mode"
              >
                {DELIVERY_MODES.map((m) => (
                  <option key={m.value || "all"} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">التوفّر</label>
              <select
                value={availability}
                onChange={(e) => updateParam("availability", e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="opp-filter-availability"
              >
                {AVAILABILITY.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="opp-active-filters">
              <span className="text-xs text-slate-500">فلاتر مفعّلة:</span>
              {activeFilters.map(([key, val]) => (
                <span key={key} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-semibold border border-teal-100">
                  {val}
                  <button
                    onClick={() => updateParam(key, key === "availability" ? "all" : "")}
                    className="hover:bg-teal-100 rounded-full"
                    aria-label={`إزالة ${val}`}
                    data-testid={`opp-remove-filter-${key}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800 ms-auto"
                data-testid="opp-clear-filters"
              >
                <RotateCcw className="h-3.5 w-3.5" /> مسح الكل
              </button>
            </div>
          )}
        </div>
      </section>

      <section className={embedded ? "pt-6" : "max-w-7xl mx-auto px-6 lg:px-10 py-12"}>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="opps-skeleton">
            {[1,2,3,4,5,6].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
                <div className="h-48 bg-cream-100 animate-pulse" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-cream-100 rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-cream-100 rounded animate-pulse" />
                  <div className="h-3 bg-cream-100 rounded animate-pulse w-5/6" />
                  <div className="pt-4 flex items-center justify-between">
                    <div className="h-3 bg-cream-100 rounded animate-pulse w-1/3" />
                    <div className="h-4 bg-cream-100 rounded animate-pulse w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-12 rounded-2xl bg-white border border-red-100 text-center" data-testid="opps-error">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900">تعذّر تحميل الفرص</h3>
            <p className="text-slate-600 mt-2">{error}</p>
            <button
              onClick={load}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600"
              data-testid="opps-retry-btn"
            >
              <RotateCcw className="h-4 w-4" /> إعادة المحاولة
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 rounded-2xl bg-white border border-slate-200 text-center" data-testid="opps-empty">
            <Sparkles className="h-10 w-10 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900">
              {activeFilters.length > 0 ? "لا توجد فرص مطابقة للفلاتر" : "لا توجد فرص متاحة حالياً"}
            </h3>
            <p className="text-slate-600 mt-2">
              {activeFilters.length > 0 ? "جرّب توسيع نطاق البحث أو مسح بعض الفلاتر." : "تحقق من الصفحة لاحقاً لاستكشاف فرص جديدة."}
            </p>
            {activeFilters.length > 0 && (
              <button
                onClick={clearFilters}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600"
                data-testid="opps-empty-clear"
              >
                <RotateCcw className="h-4 w-4" /> مسح الفلاتر
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-slate-600">
              <span className="arabic-numerals font-bold text-slate-900">{filtered.length}</span> فرصة تدريب تعاوني متاحة
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="opps-grid">
              {filtered.map((o) => (
                <OpportunityCard key={o.id} opp={o} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export function OpportunityCard({ opp }) {
  const allocations = opportunityAllocations(opp);
  const cities = distinctCities(opp);
  const modes = distinctModes(opp);
  const available = totalAvailableSeats(opp);
  const total = totalSeats(opp);

  return (
    <Link
      to={`/opportunities/${opp.id}`}
      className="group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:card-shadow-lg hover:border-teal-200 transition-all duration-300"
      data-testid={`opp-card-${opp.id}`}
    >
      <div className="relative h-48 bg-slate-800 overflow-hidden">
        <img
          src={opp.cover_image_url || "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800"}
          alt=""
          className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          aria-hidden="true"
        />
        <div
          className="absolute inset-x-0 top-0 h-24 pointer-events-none bg-gradient-to-b from-slate-950/45 to-transparent"
          aria-hidden="true"
        />
        {opp.category && (
          <div className="absolute top-4 start-4 max-w-[calc(100%-2rem)] px-3 py-1 rounded-full bg-white text-teal-800 text-xs font-bold shadow-md ring-1 ring-black/5 truncate">
            {opp.category}
          </div>
        )}
      </div>
      <div className="p-4">
        {opp.association_name && (
          <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-2.5 py-1" data-testid={`opp-assoc-${opp.id}`}>
            <Users className="h-3 w-3" />
            <span className="truncate max-w-[16rem]">بواسطة {opp.association_name}</span>
          </div>
        )}
        <h3 className="text-lg font-heading font-bold text-slate-900 group-hover:text-teal-700 transition-colors line-clamp-2">
          {opp.title}
        </h3>
        <p className="mt-2 text-sm text-slate-600 line-clamp-2 leading-relaxed">{opp.description}</p>

        <div className="mt-5 space-y-2 text-sm text-slate-600">
          {(cities.length > 0 || (allocations.length === 0 && opp.city)) && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
              <span>
                {cities.length > 0 ? cities.join(" · ") : opp.city}
              </span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            {modes.map((m) => (
              <span
                key={m}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                  m === "remote"
                    ? "bg-sky-50 text-sky-700"
                    : m === "hybrid"
                    ? "bg-violet-50 text-violet-700"
                    : "bg-teal-50 text-teal-700"
                }`}
                data-testid={`opp-mode-${opp.id}-${m}`}
              >
                {MODE_LABEL[m]}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            <span>{opp.duration_weeks} أسبوع</span>
          </div>
          {opp.total_training_hours ? (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
              <span>{opp.total_training_hours} ساعة تدريبية</span>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            <span>
              {available} مقعد متاح من {total}
              {allocations.length > 0 && (
                <span className="text-xs text-slate-500"> · {allocations.length} مواقع</span>
              )}
            </span>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500">{opp.program_name}</span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 group-hover:gap-2 transition-all">
            التفاصيل
            <ChevronLeft className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
